<template>
  <HeaderPanel />
  <ToastVue />
  <div class="agent-page">
    <h1>Агент</h1>
    <p class="subtitle">Поиск через агента с рекомендациями</p>

    <div class="search-box">
      <InputText
        v-model="query"
        placeholder="Введите запрос, например: как нанять СОО"
        class="search-input"
        @keydown.enter="search"
      />
      <ButtonVue
        :label="loading ? 'Отправка...' : 'Отправить'"
        :disabled="loading"
        :loading="loading"
        @click="search"
      />
    </div>

    <MessageVue v-if="error" severity="error" class="error-msg">{{ error }}</MessageVue>

    <div v-if="!result && !loading && !error" class="placeholder">
      <i class="pi pi-comments placeholder-icon" />
      <p>Введите запрос и нажмите «Отправить»</p>
    </div>

    <div v-if="loading && !result" class="card">
      <div class="card-title">Ответ</div>
      <div class="answer loading-text">
        <i class="pi pi-spin pi-spinner" /> Генерируется ответ...
      </div>
    </div>

    <template v-if="result">
      <div class="card">
        <div class="card-title">Ответ</div>
        <div class="answer" v-html="answerHtml" />
      </div>

      <div v-if="result.sources && result.sources.length" class="card">
        <div class="card-title">
          Источники <span class="count">· {{ result.sources.length }}</span>
        </div>
        <div class="stats">
          <span>Средний score: {{ avgScore }}</span>
          <span>Всего: {{ result.sources.length }}</span>
        </div>
        <ul class="sources-list">
          <li
            v-for="(src, i) in result.sources"
            :key="i"
            class="source-item"
          >
            <div>
              <span class="source-type">{{ src.type || '—' }}</span>
            </div>
            <div class="source-title">
              <a
                v-if="src.entry_id"
                :href="entryUrl(src.entry_id)"
                target="_blank"
                rel="noopener"
              >{{ src.title }}</a>
              <span v-else>{{ src.title }}</span>
            </div>
            <div class="source-meta">
              <span class="score">{{ (src.score * 100).toFixed(1) }}%</span>
              <span v-if="src.entry_id" class="sep">·</span>
              <span v-if="src.entry_id">ID: {{ src.entry_id }}</span>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { marked } from 'marked';
import HeaderPanel from '@/components/HeaderPanel.vue';
import { sendAgentChat } from '@/services/agentChatApi';

const toast = useToast();

const query = ref('');
const loading = ref(false);
const error = ref('');
const result = ref(null);
let controller = null;

const sessionId = crypto.randomUUID?.() ?? ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
  (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
);

const entryUrl = (id) => `https://get-nechto.ru/wisdom-base?wb_entry=${encodeURIComponent(id)}`;

const avgScore = computed(() => {
  if (!result.value?.sources?.length) return '0.000';
  const sum = result.value.sources.reduce((s, src) => s + src.score, 0);
  return (sum / result.value.sources.length).toFixed(3);
});

const answerHtml = computed(() => {
  if (!result.value?.answer) return '';
  const raw = marked.parse(result.value.answer || 'Нет ответа');
  return linkifyAnswer(raw);
});

function escapeHtml(text) {
  if (typeof text !== 'string') return '';
  const d = document.createElement('div');
  d.textContent = text;
  return d.innerHTML;
}

function linkifyAnswer(html) {
  return html
    .replace(/\(#(\d+)\)/g, (_m, id) =>
      `<sup><a href="${escapeHtml(entryUrl(id))}" class="fn" target="_blank" rel="noopener">[${id}]</a></sup>`
    )
    .replace(/href=["']#(\d+)["']/g, (_m, id) =>
      `href="${escapeHtml(entryUrl(id))}" target="_blank" rel="noopener"`
    );
}

async function search() {
  const q = query.value.trim();
  if (!q) {
    toast.add({ severity: 'warn', summary: 'Запрос', detail: 'Введите запрос', life: 2500 });
    return;
  }

  if (controller) controller.abort();
  controller = new AbortController();

  loading.value = true;
  error.value = '';
  result.value = null;

  try {
    const data = await sendAgentChat(q, sessionId, controller.signal);
    result.value = data;
    toast.add({
      severity: 'success',
      summary: 'Готово',
      detail: `Найдено ${data.sources?.length || 0} источник(ов)`,
      life: 2500,
    });
  } catch (err) {
    if (err.name === 'AbortError') return;
    error.value = err.message || 'Ошибка запроса';
    result.value = null;
  } finally {
    loading.value = false;
    controller = null;
  }
}
</script>

<style scoped>
.agent-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
}

h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.subtitle {
  color: var(--p-text-muted-color, #6c757d);
  font-size: 0.875rem;
  margin-bottom: 1.75rem;
}

.search-box {
  display: flex;
  gap: 0.625rem;
  margin-bottom: 1.75rem;
}

.search-input {
  flex: 1;
}

.error-msg {
  margin-bottom: 1.25rem;
}

.card {
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #dee2e6);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}

.card-title {
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--p-text-muted-color, #6c757d);
  margin-bottom: 1rem;
}

.card-title .count {
  color: var(--p-text-muted-color, #adb5bd);
  font-weight: 400;
}

.answer {
  line-height: 1.7;
  font-size: 0.9375rem;
}

.answer :deep(ul),
.answer :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.answer :deep(li) {
  margin: 0.25rem 0;
}

.answer :deep(p) {
  margin: 0.5rem 0;
}

.answer :deep(p:first-child) {
  margin-top: 0;
}

.answer :deep(strong) {
  font-weight: 600;
}

.answer :deep(code) {
  background: var(--p-highlight-background, #f8f9fa);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.84rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.answer :deep(pre) {
  background: var(--p-highlight-background, #f8f9fa);
  border-radius: 6px;
  padding: 0.875rem 1rem;
  overflow-x: auto;
  margin: 0.75rem 0;
  border: 1px solid var(--p-content-border-color, #dee2e6);
}

.answer :deep(pre code) {
  background: none;
  padding: 0;
}

.answer :deep(blockquote) {
  border-left: 3px solid var(--p-content-border-color, #dee2e6);
  padding: 4px 0 4px 14px;
  margin: 0.625rem 0;
  color: var(--p-text-muted-color, #6c757d);
}

.answer :deep(table) {
  border-collapse: collapse;
  margin: 0.625rem 0;
  width: 100%;
  font-size: 0.875rem;
}

.answer :deep(th),
.answer :deep(td) {
  border: 1px solid var(--p-content-border-color, #dee2e6);
  padding: 0.5rem 0.75rem;
  text-align: left;
}

.answer :deep(th) {
  background: var(--p-highlight-background, #f8f9fa);
  font-weight: 600;
}

.answer :deep(h1),
.answer :deep(h2),
.answer :deep(h3),
.answer :deep(h4) {
  margin: 1.25rem 0 0.625rem;
  font-weight: 600;
}

.answer :deep(h1) { font-size: 1.25rem; }
.answer :deep(h2) { font-size: 1.0625rem; }
.answer :deep(h3) { font-size: 0.9375rem; }

.answer :deep(hr) {
  border: none;
  border-top: 1px solid var(--p-content-border-color, #dee2e6);
  margin: 1rem 0;
}

.answer :deep(a),
.answer :deep(.fn) {
  color: var(--p-primary-color, #0d6efd);
  text-decoration: none;
  font-weight: 600;
}

.answer :deep(a:hover),
.answer :deep(.fn:hover) {
  text-decoration: underline;
}

.loading-text {
  color: var(--p-text-muted-color, #6c757d);
}

.stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #6c757d);
}

.stats span {
  background: var(--p-highlight-background, #f8f9fa);
  padding: 0.375rem 0.875rem;
  border-radius: 6px;
}

.sources-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.source-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--p-content-border-color, #dee2e6);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.source-item:last-child {
  border-bottom: none;
}

.source-type {
  display: inline-block;
  font-size: 0.6875rem;
  padding: 2px 8px;
  border-radius: 4px;
  background: var(--p-highlight-background, #f8f9fa);
  color: var(--p-text-color, #495057);
  font-weight: 500;
}

.source-title {
  font-weight: 500;
  font-size: 0.875rem;
}

.source-title a {
  color: var(--p-primary-color, #0d6efd);
  text-decoration: none;
}

.source-title a:hover {
  text-decoration: underline;
}

.source-meta {
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #6c757d);
}

.source-meta .score {
  color: #d29922;
  font-weight: 500;
}

.source-meta .sep {
  margin: 0 6px;
}

.placeholder {
  text-align: center;
  padding: 3rem 1.25rem;
  color: var(--p-text-muted-color, #adb5bd);
}

.placeholder-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.4;
  display: block;
}

.placeholder p {
  font-size: 0.875rem;
}

@media (max-width: 600px) {
  .search-box {
    flex-direction: column;
  }

  .agent-page {
    padding: 1.25rem 0.75rem 2rem;
  }

  .card {
    padding: 1rem;
  }
}
</style>
